// @flow
import * as React from 'react';
import { withLeaflet } from 'react-leaflet';
import {
    injectIntl, defineMessages, type IntlShape, FormattedMessage,
} from 'react-intl';
import styled from 'styled-components';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { debounce } from 'lodash';
import ZoomIn from 'esm-web-pro/assets/images/map/zoom/in.svg';
import ZoomOut from 'esm-web-pro/assets/images/map/zoom/out.svg';
import ZoomOutDisabled from 'esm-web-pro/assets/images/map/zoom/out-disabled.svg';
import ZoomOutRed from 'esm-web-pro/assets/images/map/zoom/out-red.svg';
import ZoomToFit from 'esm-web-pro/assets/images/map/zoom/fit.svg';
import LabelText from 'esm-web-pro/components/layout/sidePanel/LabelText';
import ReactTooltip from 'react-tooltip';

type Props = {
    leaflet: LeafletContext,
    intl: IntlShape,
}

const ZoomLevelSpan = styled.span`
    width: 30px;
    height: 30px;
    line-height: 30px;
    background-color: #fff;
    border-bottom: 1px solid #ccc;
    text-align: center;
    text-decoration: none;
    color: black;
    display: block;
`;


const messages = defineMessages({
    zoomIn: {
        id: 'map.zoom-in',
        defaultMessage: 'Zoom in',
    },
    zoomOut: {
        id: 'map.zoom-out',
        defaultMessage: 'Zoom out',
    },
    zoomToFit: {
        id: 'map.zoom-to-fit',
        defaultMessage: 'Zoom to fit',
    },
});

export const MAX_ZOOM_LEVEL = 23;

type State = {
    redFlash: false,
}

@withLeaflet
@injectIntl
class ZoomControl extends React.Component<Props, State> {
    control: Object;

    activeTimeOut: TimeoutID | null = null;

    active = false;

    static defaultProps = {
        leaflet: {},
        intl: {},
    };

    constructor(props) {
        super(props);

        this.state = {
            redFlash: false,
        };
    }

    componentDidMount(): void {
        const { leaflet: { map } } = this.props;

        map.getContainer().addEventListener('wheel', this.handleRedFlash);
        this.stopRedFlash = debounce(this.stopRedFlash, 80);
    }

    stopRedFlash = () => {
        this.setState({ redFlash: false });
    };

    handleRedFlash = (event) => {
        const { leaflet: { map } } = this.props;
        const minZoom = map.getMinZoom();
        if (minZoom > 0 && minZoom >= map.getZoom() && event.wheelDeltaY < 0) {
            this.setState({ redFlash: true });
            this.stopRedFlash();
        }
        return null;
    };

    handleZoomInClick = (e: SyntheticMouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        const { leaflet: { map } } = this.props;

        if (map) {
            map.zoomIn();
        }
    };

    handleZoomOutClick = (e: SyntheticMouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        const { leaflet: { map } } = this.props;

        if (map) {
            map.zoomOut();
        }
    };

    handleZoomToFitClick = (e: SyntheticMouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        const { leaflet: { map } } = this.props;

        if (map && map.highlightedGeometry) {
            map.flyToBoundsCenter(map.highlightedGeometry.bounds, map.highlightedGeometry.center, true);
        }
    };

    getZoomOutIcon = () => {
        const { redFlash } = this.state;
        const { leaflet: { map } } = this.props;
        const minZoom = map.getMinZoom();

        if (redFlash) return ZoomOutRed;
        if (minZoom > 0 && minZoom >= map.getZoom()) {
            return ZoomOutDisabled;
        }
        return ZoomOut;
    };

    render() {
        const { leaflet: { map }, intl: { formatMessage } } = this.props;

        const zoom = map ? map.getZoom() : 0;

        return (
            <div className="leaflet-control-zoom leaflet-bar">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                    href="#"
                    title={formatMessage[messages.zoomIn]}
                    role="button"
                    aria-label={formatMessage[messages.zoomIn]}
                    onClick={this.handleZoomInClick}
                    data-tip
                    data-for="zoom-in-tooltip"
                >
                    <img src={ZoomIn} alt={formatMessage[messages.zoomIn]} />
                </a>
                <ReactTooltip
                    id="zoom-in-tooltip"
                    effect="solid"
                    place="right"
                    className="react-tooltip"
                >
                    <LabelText size="small">
                        <FormattedMessage
                            id="zoom-in-control"
                            defaultMessage="Zoom in"
                        />
                    </LabelText>
                </ReactTooltip>
                <ZoomLevelSpan>
                    {zoom}
                </ZoomLevelSpan>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                    href="#"
                    title={formatMessage[messages.zoomOut]}
                    role="button"
                    aria-label={formatMessage[messages.zoomOut]}
                    onClick={this.handleZoomOutClick}
                    data-tip
                    data-for="zoom-out-tooltip"
                >
                    <img
                        src={this.getZoomOutIcon()}
                        alt={formatMessage[messages.zoomOut]}
                    />
                </a>
                <ReactTooltip
                    id="zoom-out-tooltip"
                    effect="solid"
                    place="right"
                    className="react-tooltip"
                >
                    <LabelText size="small">
                        <FormattedMessage
                            id="zoom-out-control"
                            defaultMessage="Zoom out"
                        />
                    </LabelText>
                </ReactTooltip>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                    href="#"
                    title={formatMessage[messages.zoomToFit]}
                    role="button"
                    aria-label={formatMessage[messages.zoomToFit]}
                    onClick={this.handleZoomToFitClick}
                    data-tip
                    data-for="zoom-to-fit-tooltip"
                >
                    <img src={ZoomToFit} alt={formatMessage[messages.zoomToFit]} />
                </a>
                <ReactTooltip
                    id="zoom-to-fit-tooltip"
                    effect="solid"
                    place="right"
                    className="react-tooltip"
                >
                    <LabelText size="small">
                        <FormattedMessage
                            id="zoom-to-fit-control"
                            defaultMessage="Zoom to fit"
                        />
                    </LabelText>
                </ReactTooltip>
            </div>
        );
    }
}

export default ZoomControl;
